import React, { useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import api from '@/lib/api';

// Pages
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import AuthCallback from '@/pages/AuthCallback';
import SearchPage from '@/pages/SearchPage';
import WordDetail from '@/pages/WordDetail';
import LearnHub from '@/pages/LearnHub';
import LessonDetail from '@/pages/LessonDetail';
import ReadingPractice from '@/pages/ReadingPractice';
import Community from '@/pages/Community';
import QA from '@/pages/QA';
import Profile from '@/pages/Profile';
import Admin from '@/pages/Admin';
import Validate from '@/pages/Validate';

// Components
import TopNav from '@/components/TopNav';
import BottomNav from '@/components/BottomNav';
import InstallBanner from '@/components/InstallBanner';

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function ForcePasswordChange() {
  const { mustChangePassword, setMustChangePassword } = useAuth();
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!mustChangePassword) return null;

  const handleSubmit = async () => {
    setError('');
    if (newPw.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPw, new_password: newPw });
      setMustChangePassword(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="force-password-change">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
        <h2 className="text-lg font-bold text-[#1C1917] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Change Your Password</h2>
        <p className="text-xs text-[#57534E] mb-4">Your password was reset by an administrator. Please create a new password to continue.</p>
        {error && <p className="text-xs text-red-600 bg-red-50 p-2 rounded-lg mb-3">{error}</p>}
        <div className="space-y-2.5">
          <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
            placeholder="Temporary password (from admin)" className="w-full rounded-xl border-0 py-2.5 px-3 text-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]" />
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password (min 6 characters)" className="w-full rounded-xl border-0 py-2.5 px-3 text-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]" />
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            placeholder="Confirm new password" className="w-full rounded-xl border-0 py-2.5 px-3 text-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50]" />
          <button onClick={handleSubmit} disabled={saving || !currentPw || !newPw || !confirmPw}
            className="w-full py-3 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl font-semibold text-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Set New Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const hideNav = ['/login', '/register'].includes(location.pathname);

  return (
    <>
      {!hideNav && <TopNav />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/dictionary" element={<SearchPage />} />
        <Route path="/dictionary/:wordId" element={<WordDetail />} />
        <Route path="/learn" element={<LearnHub />} />
        <Route path="/lessons" element={<LearnHub />} />
        <Route path="/lessons/:lessonId" element={<LessonDetail />} />
        <Route path="/practice" element={<ReadingPractice />} />
        <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
        <Route path="/qa" element={<ProtectedRoute><QA /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/validate" element={<ProtectedRoute><Validate /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!hideNav && <BottomNav />}
      <InstallBanner />
      <ForcePasswordChange />
      <Toaster />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
